import { v4 as uuidv4 } from 'uuid';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { updatePublicAPI } from '@/service/publicService';
import { createTmpUploadedFiles } from '../../../handler/ChatAIHandler';
import {
    validateDocumentsOnly,
    isValidDocumentFile,
    VALID_DOCUMENT_EXTENSIONS,
    MAX_UPLOAD_COUNT,
    MAX_FILE_SIZE
} from './UploadValidationHandler';

const VALID_FILE_ACCEPT = '.pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.pptx,.ppt';

/**
 * Validate upload files using strict document validation
 * @param {File[]} files - List of files to validate
 * @param {Object[]} currentUploadFiles - Current uploaded files
 * @param {Object[]} currentUploadImgs - Current uploaded images
 * @param {Function} enqueueSnackbar - Snackbar function for showing errors
 * @returns {boolean} - true if valid, false otherwise
 */
const validateUploadFiles = (files = [], currentUploadFiles = [], currentUploadImgs = [], enqueueSnackbar) => {
    const result = validateDocumentsOnly({
        newFiles: files,
        currentUploadImgs,
        currentUploadFiles,
        enqueueSnackbar
    });

    return result.isValid && result.documentFiles.length > 0;
};

/**
 * Create temporary file objects from files
 * @param {File[]} files - List of files
 * @returns {Object[]} - List of file objects with id, fileName, fileData, fileExtension
 */
const createTmpFileObjects = (files) => {
    return files.map(file => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        return {
            id: `tmp_file_${uuidv4()}`,
            fileName: file.name,
            fileData: file,
            fileExtension: fileExtension,
            fileSize: file.size
        };
    });
};

/**
 * Get truncated file name (max 15 characters)
 * @param {string} fileName - Original file name
 * @returns {string} - Truncated file name
 */
const getTruncatedFileName = (fileName) => {
    if (!fileName) return '';
    const dotIndex = fileName.lastIndexOf('.');
    const nameWithoutExt = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
    const extension = dotIndex > 0 ? fileName.substring(dotIndex) : '';

    if (nameWithoutExt.length <= 15) {
        return fileName;
    }
    return `${nameWithoutExt.substring(0, 15)}...${extension}`;
};

/**
 * Handle drop files (drag & drop)
 * @param {File[]} acceptedFiles - Accepted files from dropzone
 * @param {Object} params - Parameters
 * @param {Object[]} params.uploadFiles - Current uploaded files
 * @param {Object[]} params.uploadImgs - Current uploaded images
 * @param {Function} params.setUploadFiles - Set uploaded files function
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @param {Object} params.corporationDetail - Corporation detail
 * @param {Object} params.projectInuseDetail - Project in use detail
 * @param {string} params.chatThreadId - Chat thread ID
 * @param {Function} params.setIsUploading - Set uploading state
 * @param {Object} params.inputRef - Input ref to focus after drop
 */
const handleDropFiles = (acceptedFiles, params) => {
    const {
        uploadFiles,
        uploadImgs = [],
        setUploadFiles,
        enqueueSnackbar,
        corporationDetail,
        projectInuseDetail,
        chatThreadId,
        setIsUploading,
        inputRef
    } = params;

    if (!acceptedFiles || Utils.isEmptyArray(acceptedFiles)) return;

    // Filter only valid document files by extension
    const documentFiles = acceptedFiles.filter(file => isValidDocumentFile(file));

    if (Utils.isEmptyArray(documentFiles)) return;

    const isValidFiles = validateUploadFiles(documentFiles, uploadFiles, uploadImgs, enqueueSnackbar);
    if (!isValidFiles) return;

    const files = createTmpFileObjects(documentFiles);
    setUploadFiles(prev => [...prev, ...files]);

    if (inputRef?.current) {
        inputRef.current.focus();
    }

    createTmpUploadedFiles(
        corporationDetail.id,
        projectInuseDetail.id,
        chatThreadId,
        files,
        setUploadFiles,
        setIsUploading
    );
};

/**
 * Handle paste files from clipboard
 * @param {ClipboardEvent} event - Clipboard event
 * @param {Object} params - Parameters
 * @param {Object[]} params.uploadFiles - Current uploaded files
 * @param {Object[]} params.uploadImgs - Current uploaded images
 * @param {Function} params.setUploadFiles - Set uploaded files function
 * @param {Function} params.enqueueSnackbar - Snackbar function
 * @param {Object} params.corporationDetail - Corporation detail
 * @param {Object} params.projectInuseDetail - Project in use detail
 * @param {string} params.chatThreadId - Chat thread ID
 * @param {Function} params.setIsUploading - Set uploading state
 * @param {Object} params.inputRef - Input ref to focus after paste
 * @returns {boolean} - true if handled paste files, false otherwise
 */
const handlePasteFiles = (event, params) => {
    const {
        uploadFiles,
        uploadImgs = [],
        setUploadFiles,
        enqueueSnackbar,
        corporationDetail,
        projectInuseDetail,
        chatThreadId,
        setIsUploading,
        inputRef
    } = params;

    const items = event.clipboardData.items;
    let listPastedFiles = [];

    for (const item of items) {
        if (item.kind === 'file') {
            const file = item.getAsFile();
            // Filter by extension only
            if (isValidDocumentFile(file)) {
                listPastedFiles.push(file);
            }
        }
    }

    if (listPastedFiles.length) {
        const isValidFiles = validateUploadFiles(listPastedFiles, uploadFiles, uploadImgs, enqueueSnackbar);
        if (!isValidFiles) {
            event.preventDefault();
            return true;
        }

        const createFiles = createTmpFileObjects(listPastedFiles);
        setUploadFiles(prev => [...prev, ...createFiles]);

        createTmpUploadedFiles(
            corporationDetail.id,
            projectInuseDetail.id,
            chatThreadId,
            createFiles,
            setUploadFiles,
            setIsUploading
        );

        event.preventDefault();
        if (inputRef?.current) {
            inputRef.current.focus();
        }
        return true;
    }

    return false;
};

/**
 * Remove uploaded file
 * @param {Object} removeFile - File to remove
 * @param {Function} setUploadFiles - Set uploaded files function
 */
const removeUploadedFile = (removeFile, setUploadFiles) => {
    setUploadFiles(prevFiles => prevFiles.filter(file => file.id !== removeFile.id));
    if (removeFile.s3Path) {
        updatePublicAPI(null, {
            updateApiType: Constant.UPDATE_API_PUBLIC_TYPE.DELETE_S3_FILE_BY_PATH,
            s3Path: removeFile.s3Path
        });
    }
};

/**
 * Cleanup uploaded files
 * @param {Object[]} uploadFiles - List of uploaded files
 * @param {Function} setUploadFiles - Set uploaded files function
 */
const cleanupUploadedFiles = (uploadFiles, setUploadFiles) => {
    if (Utils.isEmptyArray(uploadFiles)) return;
    setUploadFiles([]);
};

export {
    VALID_FILE_ACCEPT,
    VALID_DOCUMENT_EXTENSIONS,
    MAX_UPLOAD_COUNT,
    MAX_FILE_SIZE,
    validateUploadFiles,
    createTmpFileObjects,
    getTruncatedFileName,
    handleDropFiles,
    handlePasteFiles,
    removeUploadedFile,
    cleanupUploadedFiles
};
